import { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { Alerts } from "./types";
import { StockOutList } from "./stock_out_list";
import { ExcessInventoryList } from "./excess_inventory_list";
import { formatToMillions } from "../../../utils/numbers";

export const AlertsSection = ({ alerts }: { alerts: Alerts }) => {
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false);

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  const hasAlerts =
    alerts.stock_outs.count > 0 ||
    alerts.excess_inventory.count > 0 ||
    alerts.low_stock.count > 0;

  if (!hasAlerts) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Exceptions & Alerts
      </Typography>

      {/* Stock Outs */}
      {alerts.stock_outs.count > 0 && (
        <Accordion
          expanded={expandedPanel === "stock_outs"}
          onChange={handleChange("stock_outs")}
          sx={{ mb: 1 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "& .MuiAccordionSummary-content": {
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 1,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ErrorIcon sx={{ color: "error.main" }} />
              <Typography sx={{ fontWeight: 500, minWidth: "100px" }}>
                Stock Outs
              </Typography>
            </Box>
            <Badge
              badgeContent={alerts.stock_outs.count}
              color="error"
              max={999}
              sx={{ ml: 1, mr: 2 }}
            >
              <Box sx={{ width: 24, height: 24 }} />
            </Badge>
            {alerts.stock_outs.total_value && (
              <Chip
                label={`${formatToMillions(alerts.stock_outs.total_value)} at risk`}
                size="small"
                color="error"
                variant="outlined"
                sx={{ ml: "auto" }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <StockOutList alerts={alerts.stock_outs.items} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Excess Inventory */}
      {alerts.excess_inventory.count > 0 && (
        <Accordion
          expanded={expandedPanel === "excess_inventory"}
          onChange={handleChange("excess_inventory")}
          sx={{ mb: 1 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "& .MuiAccordionSummary-content": {
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 1,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <WarningIcon sx={{ color: "warning.main" }} />
              <Typography sx={{ fontWeight: 500, minWidth: "140px" }}>
                Excess Inventory
              </Typography>
            </Box>
            <Badge
              badgeContent={alerts.excess_inventory.count}
              color="warning"
              max={999}
              sx={{ ml: 1, mr: 2 }}
            >
              <Box sx={{ width: 24, height: 24 }} />
            </Badge>
            {alerts.excess_inventory.total_value && (
              <Chip
                label={`${formatToMillions(alerts.excess_inventory.total_value)} excess`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ ml: "auto" }}
              />
            )}
          </AccordionSummary>
          <AccordionDetails>
            <ExcessInventoryList alerts={alerts.excess_inventory.items} />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Low Stock Warnings */}
      {alerts.low_stock.count > 0 && (
        <Accordion
          expanded={expandedPanel === "low_stock"}
          onChange={handleChange("low_stock")}
          sx={{ mb: 1 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              "& .MuiAccordionSummary-content": {
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 1,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <InfoIcon sx={{ color: "info.main" }} />
              <Typography sx={{ fontWeight: 500, minWidth: "160px" }}>
                Low Stock Warnings
              </Typography>
            </Box>
            <Badge
              badgeContent={alerts.low_stock.count}
              color="info"
              max={999}
              sx={{ ml: 1, mr: 2 }}
            >
              <Box sx={{ width: 24, height: 24 }} />
            </Badge>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">
              {alerts.low_stock.items.length} SKU-location combinations below threshold
            </Typography>
            {/* Can add LowStockList component similar to others if needed */}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
